"use client";

import { startTransition, useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { createPortal } from "react-dom";
import { X, ChevronDown, ChevronUp, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAnimatedOpen } from "@/components/ui/use-animated-open";
// 2026-10-03 FIX F12: patrón existente de diálogos custom (GiftProNotice,
// chat-sidebar lightbox) — trap Tab + Escape + retorno al trigger.
import { useFocusTrapDialog } from "@/components/chat/hooks/use-focus-trap-dialog";
import { useT } from "@/lib/use-i18n";

const COOKIE_CONSENT_VERSION = 1;

interface ConsentPreferences {
  version: number;
  essential: true;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
  timestamp: number;
}

const DEFAULT_CONSENT: ConsentPreferences = {
  version: COOKIE_CONSENT_VERSION,
  essential: true,
  analytics: false,
  marketing: false,
  preferences: false,
  timestamp: Date.now(),
};

function getStoredConsent(): ConsentPreferences | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem("vortixy-cookie-consent");
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (
      !parsed ||
      typeof parsed !== "object" ||
      Array.isArray(parsed) ||
      parsed.version !== COOKIE_CONSENT_VERSION
    ) {
      return null;
    }
    // 2026-10-28 security hardening: only copy known consent keys from the
    // parsed payload — never spread arbitrary keys (prototype-pollution /
    // unexpected-property injection hardening).
    const safe: ConsentPreferences = { ...DEFAULT_CONSENT };
    if (typeof parsed.essential === "boolean")
      safe.essential = parsed.essential;
    if (typeof parsed.analytics === "boolean")
      safe.analytics = parsed.analytics;
    if (typeof parsed.marketing === "boolean")
      safe.marketing = parsed.marketing;
    if (typeof parsed.preferences === "boolean")
      safe.preferences = parsed.preferences;
    if (
      typeof parsed.timestamp === "number" &&
      Number.isFinite(parsed.timestamp)
    ) {
      safe.timestamp = parsed.timestamp;
    }
    return safe;
  } catch {
    return null;
  }
}

function setStoredConsent(consent: ConsentPreferences) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem("vortixy-cookie-consent", JSON.stringify(consent));
  } catch {
    // localStorage unavailable
  }
}

function hasConsent(
  category: keyof Omit<ConsentPreferences, "version" | "timestamp">,
): boolean {
  const consent = getStoredConsent();
  if (!consent) return false;
  return consent[category] === true;
}

function loadAnalytics() {
  if (!hasConsent("analytics")) return;
  // Analytics will load via VercelInsights component which checks consent
  window.dispatchEvent(new CustomEvent("vortixy:consent:analytics"));
}

function loadMarketing() {
  if (!hasConsent("marketing")) return;
  window.dispatchEvent(new CustomEvent("vortixy:consent:marketing"));
}

export function CookieConsent() {
  const t = useT();
  const pathname = usePathname();
  const isChatPage =
    pathname === "/chat" || /^\/[a-z]{2}\/chat\/?$/.test(pathname ?? "");
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState<ConsentPreferences>(DEFAULT_CONSENT);
  const [mounted] = useState(() => typeof document !== "undefined");

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored) {
      // Show immediately so mobile users see it without scrolling to the page end.
      // Tiny rAF delay avoids layout thrash on first paint only.
      const id = window.requestAnimationFrame(() => setVisible(true));
      return () => window.cancelAnimationFrame(id);
    }
    startTransition(() => {
      setPrefs(stored);
    });
    if (stored.analytics) loadAnalytics();
    if (stored.marketing) loadMarketing();
  }, []);

  const saveConsent = (newPrefs: Partial<ConsentPreferences>) => {
    const updated = { ...prefs, ...newPrefs, timestamp: Date.now() };
    setPrefs(updated);
    setStoredConsent(updated);
    // Always notify listeners (VercelInsights, etc.) so accept AND reject re-evaluate.
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("vortixy:consent:changed", { detail: updated }),
      );
    }
    if (updated.analytics === true) loadAnalytics();
    if (updated.marketing === true) loadMarketing();
    setVisible(false);
  };

  const acceptAll = () => {
    saveConsent({ analytics: true, marketing: true, preferences: true });
  };

  const rejectAll = () => {
    saveConsent({ analytics: false, marketing: false, preferences: false });
  };

  const saveCustom = () => {
    saveConsent({
      analytics: prefs.analytics,
      marketing: prefs.marketing,
      preferences: prefs.preferences,
    });
  };

  const { shouldRender, isLeaving } = useAnimatedOpen(visible, 150);

  // 2026-10-03 FIX F12 (research 2026 WHATWG HTML §6.3 inert living standard
  // 2026-09-22 + W3C APG dialog-modal): el panel de preferencias es el tramo
  // dialog-like del banner — trap Tab + Escape (colapsa sin guardar) +
  // retorno al trigger vía patrón useFocusTrapDialog. Alcance SOLO expanded:
  // el banner es modeless (aria-modal=false, no roba foco al cargar — WCAG
  // 2.4.3) y la página sigue usable detrás, así que NO se marca el fondo
  // como inert (inert es para modal; WHATWG §6.3.2). 0 cambio visual.
  const dialogRef = useFocusTrapDialog<HTMLDivElement>(expanded, () =>
    setExpanded(false),
  );

  if (!shouldRender || !mounted) return null;

  // Portal to body so sticky headers / mobile CTA / transforms never trap the banner.
  // z-[10050] sits above mobile-sticky-cta (45), tours (10000), update prompts (9999).
  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-label={t("ariaCookieConsent")}
      aria-modal="false"
      data-testid="cookie-consent"
      data-chat-page={isChatPage ? "true" : undefined}
      // 2026-09-16 fondo opaco: modal-surface (en dark .lovable-card es 75% + blur y la página se transparentaba detrás del banner).
      className={`cookie-consent-banner fixed inset-x-3 z-[10050] mx-auto max-w-md lovable-card lovable-card--static modal-surface p-4 shadow-[var(--shadow-panel)] sm:inset-x-auto sm:bottom-6 sm:right-6 sm:left-auto sm:p-5 ${isChatPage ? "max-sm:bottom-[calc(env(safe-area-inset-bottom,0px)+6rem)]" : ""} ${isLeaving ? "animate-out fade-out slide-out-to-bottom-1 duration-150 motion-reduce:animate-none" : "animate-in fade-in slide-in-from-bottom-1 duration-150 motion-reduce:animate-none"}`}
      style={{
        // The chat composer is fixed to the bottom edge on mobile, so the
        // consent banner sits above it. Other routes keep the home-indicator offset.
        bottom: isChatPage
          ? undefined
          : "max(1rem, env(safe-area-inset-bottom, 0px))",
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-sm font-semibold text-foreground">
            {t("cookieConsentTitle")}
          </p>
          <p className="text-xs leading-5 text-foreground-soft">
            {t("cookieConsentBody")}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={() => {
            // X = essential-only (same as reject). Never re-show every pageview —
            // that was a cold-traffic bounce driver.
            rejectAll();
          }}
          aria-label={t("cookieConsentDismiss")}
          className="shrink-0"
        >
          <X className="size-4" aria-hidden="true" />
        </Button>
      </div>

      {!expanded ? (
        <div className="mt-4 space-y-2.5">
          <div
            className={`flex flex-col gap-2 sm:flex-row ${isChatPage ? "max-sm:flex-row" : ""}`}
          >
            {/* 2026-10-03 FIX F8 (research 2026 MDN overflow-wrap 2026-07-21):
                en /chat 360px la fila forzada con 2 botones DE largos
                desbordaba — min-w-0 deja encoger al flex-1, truncate elide
                en 1 línea y title conserva el texto completo. En desktop las
                etiquetas caben: 0 cambio visual. */}
            <Button
              type="button"
              size="sm"
              className="min-h-11 min-w-0 flex-1 truncate sm:min-h-9"
              title={t("cookieConsentAcceptAll")}
              onClick={acceptAll}
            >
              {t("cookieConsentAcceptAll")}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="min-h-11 min-w-0 flex-1 truncate sm:min-h-9"
              title={t("cookieConsentRejectAll")}
              onClick={rejectAll}
            >
              {t("cookieConsentRejectAll")}
            </Button>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full min-h-10"
            onClick={() => setExpanded(true)}
          >
            {t("cookieConsentCustomize")}{" "}
            {/* 2026-10-03 FIX F7 (research 2026 MDN margin-inline-end: las
                props lógicas espejan en ar-RTL; ml-1 físico no). */}
            <ChevronDown className="size-4 ms-1" aria-hidden="true" />
          </Button>
        </div>
      ) : (
        <div className="mt-4 max-h-[min(55dvh,22rem)] space-y-3 overflow-y-auto border-t border-[var(--border)] pt-4 overscroll-contain">
          <p className="text-xs font-medium text-muted-foreground">
            {t("cookieConsentCategories")}
          </p>

          <div className="space-y-2">
            {/* 2026-09-24 FASE 3 FIX design-auth-tokens (research 2026 DSP
                radius tokens: rounded-xl → var(--radius-panel) 16px SSOT). */}
            <div className="flex items-center justify-between rounded-[var(--radius-panel)] bg-[var(--muted)] p-2.5">
              <div className="min-w-0 pr-2">
                <p className="text-sm font-medium text-foreground">
                  {t("cookieEssentialTitle")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("cookieEssentialDesc")}
                </p>
              </div>
              {/* 2026-09-23 FASE 2 F-05 (research 2026 WCAG 2.2 AA 2.5.8:
                  checkbox visual 24px mínimo; el área táctil coarse 44px la da
                  la fila <label> completa — clicable, p-2.5 + 2 líneas ≈60px —
                  sin inflar el glifo a 44px que rompería la fila). */}
              <input
                type="checkbox"
                checked={true}
                disabled
                className="size-6 shrink-0 accent-[var(--accent)]"
                aria-label={t("cookieEssentialTitle")}
              />
            </div>

            <label className="flex cursor-pointer items-center justify-between rounded-[var(--radius-panel)] bg-[var(--muted)] p-2.5 hover:bg-[var(--muted)]/80">
              <div className="min-w-0 pr-2">
                <p className="text-sm font-medium text-foreground">
                  {t("cookieAnalyticsTitle")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("cookieAnalyticsDesc")}
                </p>
              </div>
              <input
                type="checkbox"
                checked={prefs.analytics}
                onChange={(e) =>
                  setPrefs({ ...prefs, analytics: e.target.checked })
                }
                className="size-6 shrink-0 accent-[var(--accent)]"
                aria-label={t("cookieAnalyticsTitle")}
              />
            </label>

            <label className="flex cursor-pointer items-center justify-between rounded-[var(--radius-panel)] bg-[var(--muted)] p-2.5 hover:bg-[var(--muted)]/80">
              <div className="min-w-0 pr-2">
                <p className="text-sm font-medium text-foreground">
                  {t("cookieMarketingTitle")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("cookieMarketingDesc")}
                </p>
              </div>
              <input
                type="checkbox"
                checked={prefs.marketing}
                onChange={(e) =>
                  setPrefs({ ...prefs, marketing: e.target.checked })
                }
                className="size-6 shrink-0 accent-[var(--accent)]"
                aria-label={t("cookieMarketingTitle")}
              />
            </label>

            <label className="flex cursor-pointer items-center justify-between rounded-[var(--radius-panel)] bg-[var(--muted)] p-2.5 hover:bg-[var(--muted)]/80">
              <div className="min-w-0 pr-2">
                <p className="text-sm font-medium text-foreground">
                  {t("cookiePreferencesTitle")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("cookiePreferencesDesc")}
                </p>
              </div>
              <input
                type="checkbox"
                checked={prefs.preferences}
                onChange={(e) =>
                  setPrefs({ ...prefs, preferences: e.target.checked })
                }
                className="size-6 shrink-0 accent-[var(--accent)]"
                aria-label={t("cookiePreferencesTitle")}
              />
            </label>
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="min-h-11 flex-1 sm:min-h-9"
              onClick={() => setExpanded(false)}
            >
              <ChevronUp className="size-4 me-1" aria-hidden="true" />{" "}
              {t("cookieConsentBack")}
            </Button>
            <Button
              type="button"
              size="sm"
              className="min-h-11 flex-1 sm:min-h-9"
              onClick={saveCustom}
            >
              <Check className="size-4 me-1" aria-hidden="true" />{" "}
              {t("cookieConsentSave")}
            </Button>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}

export { hasConsent, loadAnalytics, loadMarketing };
